// ============================================================================
// src/stores/DisputeStore.ts - DISPUTE MANAGEMENT STORE
// ============================================================================

import { makeAutoObservable, runInAction } from 'mobx';
import {
  Dispute,
  DisputeFilters,
  CreateDisputeData
} from '@/types/disputes';
import { disputeService } from '@/services/disputeService';
import { RootStore } from '.';

/**
 * DisputeStore - Quản lý báo cáo và tranh chấp
 * 
 * Features:
 * - Create và manage disputes
 * - User disputes queue
 * - Admin dispute queue
 * - Status tracking
 * - Evidence upload
 */
export class DisputeStore {
  rootStore: RootStore;

  // ========================================
  // DISPUTE DATA
  // ========================================
  disputes: Dispute[] = [];
  myDisputes: Dispute[] = [];
  adminQueue: Dispute[] = [];
  selectedDispute: Dispute | null = null;

  // ========================================
  // UI STATES
  // ========================================
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  isProcessing: boolean = false;
  error: string | null = null;

  // ========================================
  // PAGINATION
  // ========================================
  currentPage: number = 1;
  pageSize: number = 20;
  totalDisputes: number = 0;
  totalPages: number = 1;
  hasMore: boolean = true;

  // Admin queue pagination
  adminCurrentPage: number = 1;
  adminTotalPages: number = 1;
  adminHasMore: boolean = true;

  // ========================================
  // FILTERS
  // ========================================
  filters: DisputeFilters = {};
  adminFilters: DisputeFilters = {};

  // ========================================
  // STATISTICS
  // ========================================
  stats: {
    totalDisputes: number;
    submitted: number;
    reviewing: number;
    resolved: number;
    closed: number;
  } | null = null;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
  }

  // ========================================
  // INITIALIZATION
  // ========================================

  /**
   * Initialize store
   */
  async initialize(isAdmin: boolean = false) {
    if (isAdmin) {
      await this.loadAdminQueue();
    } else {
      await this.loadMyDisputes();
    }
    await this.loadStats();
  }

  // ========================================
  // LOAD DISPUTES
  // ========================================

  /**
   * Load disputes của user hiện tại
   */
  async loadMyDisputes(page: number = 1) {
    this.setLoading(true);
    this.setError(null);

    try {
      const result = await disputeService.getMyDisputes(
        this.filters,
        page,
        this.pageSize
      );

      runInAction(() => {
        if (page === 1) {
          this.myDisputes = result.disputes;
        } else {
          this.myDisputes = [...this.myDisputes, ...result.disputes];
        }

        this.currentPage = result.pagination.page;
        this.totalPages = result.pagination.pageCount;
        this.totalDisputes = result.pagination.total;
        this.hasMore = result.pagination.page < result.pagination.pageCount;
      });

      console.log('✅ Loaded my disputes:', result.disputes.length);
    } catch (error: any) {
      this.setError(error.message || 'Không thể tải danh sách báo cáo');
      console.error('Load disputes error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Load admin dispute queue
   */
  async loadAdminQueue(page: number = 1) {
    this.setLoading(true);
    this.setError(null);

    try {
      const result = await disputeService.getAdminDisputeQueue(
        this.adminFilters,
        page,
        this.pageSize
      );

      runInAction(() => {
        if (page === 1) {
          this.adminQueue = result.disputes;
        } else {
          this.adminQueue = [...this.adminQueue, ...result.disputes];
        }

        this.adminCurrentPage = result.pagination.page;
        this.adminTotalPages = result.pagination.pageCount;
        this.adminHasMore = result.pagination.page < result.pagination.pageCount;
      });

      console.log('✅ Loaded admin queue:', result.disputes.length);
    } catch (error: any) {
      this.setError(error.message || 'Không thể tải hàng đợi xử lý');
      console.error('Load admin queue error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Load more disputes (pagination)
   */
  async loadMoreDisputes() {
    if (!this.hasMore || this.isLoading) {
      return;
    }

    await this.loadMyDisputes(this.currentPage + 1);
  }

  /**
   * Load more admin queue
   */
  async loadMoreAdminQueue() {
    if (!this.adminHasMore || this.isLoading) {
      return;
    }

    await this.loadAdminQueue(this.adminCurrentPage + 1);
  }

  /**
   * Refresh disputes
   */
  async refreshDisputes(isAdmin: boolean = false) {
    if (isAdmin) {
      await this.loadAdminQueue(1);
    } else {
      await this.loadMyDisputes(1);
    }
  }

  // ========================================
  // DISPUTE DETAILS
  // ========================================

  /**
   * Get dispute by documentId
   */
  async getDisputeById(documentId: string) {
    this.setLoading(true);
    this.setError(null);

    try {
      const dispute = await disputeService.getDisputeById(documentId);

      runInAction(() => {
        this.selectedDispute = dispute;
      });

      return dispute;
    } catch (error: any) {
      this.setError(error.message || 'Không thể tải thông tin báo cáo');
      console.error('Get dispute error:', error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // CREATE DISPUTE
  // ========================================

  /**
   * Create new dispute
   */
  async createDispute(data: CreateDisputeData) {
    this.setSubmitting(true);
    this.setError(null);

    try {
      const dispute = await disputeService.createDispute(data);

      runInAction(() => {
        this.myDisputes.unshift(dispute);
        this.totalDisputes += 1;
        this.selectedDispute = dispute;
      });

      console.log('✅ Dispute created:', dispute.disputeNumber);
      return dispute;
    } catch (error: any) {
      this.setError(error.message || 'Không thể tạo báo cáo');
      console.error('Create dispute error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  // ========================================
  // ADMIN ACTIONS
  // ========================================

  /**
   * Assign dispute to admin
   */
  async assignDispute(documentId: string, adminId: number) {
    this.setProcessing(true);
    this.setError(null);

    try {
      await disputeService.assignDispute(documentId, adminId);

      runInAction(() => {
        // Update trong admin queue
        const dispute = this.adminQueue.find(d => d.documentId === documentId);
        if (dispute) {
          dispute.status = 'reviewing';
          dispute.assignedTo = { 
            id: adminId, 
            username: this.rootStore.authStore.currentUser?.username || '' 
          };
        }

        // Update selected
        if (this.selectedDispute?.documentId === documentId) {
          this.selectedDispute.status = 'reviewing';
          this.selectedDispute.assignedTo = { 
            id: adminId, 
            username: this.rootStore.authStore.currentUser?.username || '' 
          };
        }
      });

      console.log('✅ Dispute assigned');
    } catch (error: any) {
      this.setError(error.message || 'Không thể phân công xử lý');
      console.error('Assign dispute error:', error);
      throw error;
    } finally {
      this.setProcessing(false);
    }
  }

  /**
   * Update dispute status
   */
  async updateDisputeStatus(
    documentId: string,
    status: string,
    adminNotes?: string
  ) {
    this.setProcessing(true);
    this.setError(null);

    try {
      await disputeService.updateDisputeStatus(documentId, status, adminNotes);

      runInAction(() => {
        // Update trong admin queue
        const dispute = this.adminQueue.find(d => d.documentId === documentId);
        if (dispute) {
          dispute.status = status as any;
          if (adminNotes) {
            dispute.adminNotes = adminNotes;
          }
          if (status === 'resolved' || status === 'closed') {
            dispute.resolvedAt = new Date().toISOString();
          }
        }

        // Update trong myDisputes
        const myDispute = this.myDisputes.find(d => d.documentId === documentId);
        if (myDispute) {
          myDispute.status = status as any;
          if (status === 'resolved' || status === 'closed') {
            myDispute.resolvedAt = new Date().toISOString();
          }
        }

        // Update selected
        if (this.selectedDispute?.documentId === documentId) {
          this.selectedDispute.status = status as any;
          if (adminNotes) {
            this.selectedDispute.adminNotes = adminNotes;
          }
          if (status === 'resolved' || status === 'closed') {
            this.selectedDispute.resolvedAt = new Date().toISOString();
          }
        }
      });

      console.log('✅ Dispute status updated');
    } catch (error: any) {
      this.setError(error.message || 'Không thể cập nhật trạng thái');
      console.error('Update status error:', error);
      throw error;
    } finally {
      this.setProcessing(false);
    }
  }

  // ========================================
  // FILTERS
  // ========================================

  /**
   * Update user filters
   */
  updateFilters(newFilters: Partial<DisputeFilters>) {
    this.filters = {
      ...this.filters,
      ...newFilters
    };

    this.loadMyDisputes(1);
  }

  /**
   * Update admin filters
   */
  updateAdminFilters(newFilters: Partial<DisputeFilters>) {
    this.adminFilters = {
      ...this.adminFilters,
      ...newFilters
    };

    this.loadAdminQueue(1);
  }

  /**
   * Clear all filters
   */
  clearFilters(isAdmin: boolean = false) {
    if (isAdmin) {
      this.adminFilters = {};
      this.loadAdminQueue(1);
    } else {
      this.filters = {};
      this.loadMyDisputes(1);
    }
  }

  /**
   * Remove một filter
   */
  removeFilter(filterKey: keyof DisputeFilters, isAdmin: boolean = false) {
    if (isAdmin) {
      const newFilters = { ...this.adminFilters };
      delete newFilters[filterKey];
      this.adminFilters = newFilters;
      this.loadAdminQueue(1);
    } else {
      const newFilters = { ...this.filters };
      delete newFilters[filterKey];
      this.filters = newFilters;
      this.loadMyDisputes(1);
    }
  }

  // ========================================
  // STATISTICS
  // ========================================

  /**
   * Load dispute statistics
   */
  async loadStats() {
    try {
      const disputes = this.myDisputes.length > 0 
        ? this.myDisputes 
        : (await disputeService.getMyDisputes({}, 1, 1000)).disputes;

      const submitted = disputes.filter(d => d.status === 'submitted').length;
      const reviewing = disputes.filter(d => d.status === 'reviewing').length;
      const resolved = disputes.filter(d => d.status === 'resolved').length;
      const closed = disputes.filter(d => d.status === 'closed').length;

      runInAction(() => {
        this.stats = {
          totalDisputes: disputes.length,
          submitted,
          reviewing,
          resolved,
          closed
        };
      });
    } catch (error) {
      console.error('Load stats error:', error);
    }
  }

  // ========================================
  // COMPUTED PROPERTIES
  // ========================================

  /**
   * Get pending disputes
   */
  get pendingDisputes(): Dispute[] {
    return this.myDisputes.filter(d => d.status === 'submitted');
  }

  /**
   * Get reviewing disputes
   */
  get reviewingDisputes(): Dispute[] {
    return this.myDisputes.filter(d => d.status === 'reviewing');
  }

  /**
   * Get resolved disputes
   */
  get resolvedDisputes(): Dispute[] {
    return this.myDisputes.filter(d => d.status === 'resolved');
  }

  /**
   * Get recent disputes (last 30 days)
   */
  get recentDisputes(): Dispute[] {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return this.myDisputes.filter(
      d => new Date(d.filedAt) >= thirtyDaysAgo
    );
  }

  /**
   * Get unassigned disputes (admin)
   */
  get unassignedDisputes(): Dispute[] {
    return this.adminQueue.filter(d => !d.assignedTo);
  }

  // ========================================
  // UTILITIES
  // ========================================

  /**
   * Get status label
   */
  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      submitted: 'Đã gửi',
      reviewing: 'Đang xem xét',
      resolved: 'Đã giải quyết',
      closed: 'Đã đóng'
    };

    return labels[status] || status;
  }

  /**
   * Get issue type label
   */
  getIssueTypeLabel(issueType: string): string {
    const labels: Record<string, string> = {
      payment_not_received: 'Chưa nhận được thanh toán',
      license_not_activated: 'License chưa kích hoạt',
      cannot_download: 'Không thể tải xuống',
      wrong_license: 'License không đúng',
      quality_issue: 'Vấn đề chất lượng',
      other: 'Khác'
    };

    return labels[issueType] || issueType;
  }

  /**
   * Format date
   */
  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('vi-VN');
  }

  // ========================================
  // STATE SETTERS
  // ========================================

  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setSubmitting(submitting: boolean) {
    this.isSubmitting = submitting;
  }

  setProcessing(processing: boolean) {
    this.isProcessing = processing;
  }

  setError(error: string | null) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  // ========================================
  // RESET
  // ========================================

  reset() {
    this.disputes = [];
    this.myDisputes = [];
    this.adminQueue = [];
    this.selectedDispute = null;
    this.isLoading = false;
    this.isSubmitting = false;
    this.isProcessing = false;
    this.error = null;
    this.currentPage = 1;
    this.pageSize = 20;
    this.totalDisputes = 0;
    this.totalPages = 1;
    this.hasMore = true;
    this.adminCurrentPage = 1;
    this.adminTotalPages = 1;
    this.adminHasMore = true;
    this.filters = {};
    this.adminFilters = {};
    this.stats = null;
  }
}

export default DisputeStore;
